import { useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import URDFLoader from 'urdf-loader';
import { URDFDragControls } from 'urdf-loader/src/URDFDragControls.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  createTofBaseCorners,
  createTofPyramidEdges,
  createTofPyramidMaterial,
  createTofRay,
} from './tofEffectGeometry';
import { startTofScanRendering } from './tofScanRendering';
import { followPreviewMovement } from './previewFollow';
import { DEFAULT_EYE_LED_COLOR, getEyeLedIntensity, getEyeLedSurfaceState } from './eyeLedState';
import { EyeBloom } from './eyeBloom';
import { ForwardEyeLight } from './forwardEyeLight';
import { applyMazePoseToAnchor, mazeGridToWorld, type MazeRobotPose } from './mazePose';
import {
  createPreviewFloor,
  disposePreviewFloor,
  PREVIEW_BACKGROUND_COLOR,
  PREVIEW_FLOOR_SIZE,
} from './previewFloor';
import {
  getMazeFreeCameraRotation,
  getRobotViewCameraPose,
  MAZE_FREE_CAMERA_CONTROLS,
  offsetRobotViewCameraPose,
  shouldRefitMazeCameraOnResize,
  type MazeCameraMode,
  type MazeCameraRotation,
} from './mazeCamera';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { WEGO_JOINTS } from './wegoJoints';
import { AmmoPhysicsWorld } from './ammoPhysics';
import { measureMazeWallDistanceMm, type MazeStageDefinition } from '../config/mazeStages';
import {
  WEGO_URDF_BASE,
  WEGO_URDF_URL,
  compileTimeline,
  createStandbyTimeline,
  interpolate,
  interpolateFromOrigin,
  normalizeWegoAssetUrl,
  resolveWegoPackagePath,
  type CompiledTimeline,
  type EyeLedKeyframe,
  type MoveTrackId,
  type TimelineData,
} from './animationUtils';

type WegoJoint = URDFJointObject & {
  ignoreLimits?: boolean;
  jointType?: string;
  angle?: number;
  jointValue?: number[];
};

type WegoRobot = THREE.Object3D & {
  joints?: Record<string, WegoJoint | undefined>;
  links?: Record<string, (THREE.Object3D & {
    inertial?: { mass?: number; origin?: { xyz?: number[] } };
  }) | undefined>;
  setJointValue?: (jointId: string, value: number) => void;
};

type URDFJointObject = THREE.Object3D & { setJointValue?: (value: number) => void };

type HighlightableMesh = THREE.Mesh & { originalMaterial?: THREE.Material | THREE.Material[] };

type SelectableJoint = URDFJointObject & {
  jointType?: string;
  angle?: number;
  jointValue?: number[];
  axis?: THREE.Vector3;
};

class CanvasPointerURDFDragControls extends URDFDragControls {
  readonly domElement: HTMLElement;
  private readonly camera: THREE.Camera;
  private readonly resolveJoint: (object: THREE.Object3D) => THREE.Object3D | null;
  private readonly canDrag: (object: THREE.Object3D) => boolean;
  private readonly pointer = new THREE.Vector2();
  private readonly pointerRaycaster = new THREE.Raycaster();

  constructor(
    scene: THREE.Object3D,
    camera: THREE.Camera,
    domElement: HTMLElement,
    resolveJoint: (object: THREE.Object3D) => THREE.Object3D | null,
    canDrag: (object: THREE.Object3D) => boolean,
  ) {
    super(scene);
    this.camera = camera;
    this.domElement = domElement;
    this.resolveJoint = resolveJoint;
    this.canDrag = canDrag;
    domElement.addEventListener('mousedown', this.handleMouseDown);
    domElement.addEventListener('mousemove', this.handleMouseMove);
    domElement.addEventListener('mouseup', this.handleMouseUp);
    domElement.addEventListener('mouseleave', this.handleMouseLeave);
  }

  private updatePointerRay(event: MouseEvent) {
    const width = Math.max(1, this.domElement.clientWidth);
    const height = Math.max(1, this.domElement.clientHeight);
    this.pointer.set(
      (event.offsetX / width) * 2 - 1,
      -(event.offsetY / height) * 2 + 1,
    );
    this.pointerRaycaster.setFromCamera(this.pointer, this.camera);
    this.moveRay(this.pointerRaycaster.ray);
  }

  private readonly handleMouseDown = (event: MouseEvent) => {
    this.updatePointerRay(event);
    if (this.hovered && this.canDrag(this.hovered)) this.setGrabbed(true);
  };

  private readonly handleMouseMove = (event: MouseEvent) => {
    this.updatePointerRay(event);
  };

  private readonly handleMouseUp = (event: MouseEvent) => {
    this.updatePointerRay(event);
    this.setGrabbed(false);
  };

  private readonly handleMouseLeave = () => {
    this.setGrabbed(false);
  };

  override update() {
    if (this.manipulating) return;
    let nextJoint: THREE.Object3D | null = null;
    const intersections = this.raycaster.intersectObject(this.scene, true);
    for (const intersection of intersections) {
      let visual: THREE.Object3D | null = intersection.object;
      let isVisibleMesh = true;
      while (visual && visual !== this.scene) {
        if (!visual.visible || (visual as { isURDFCollider?: boolean }).isURDFCollider) {
          isVisibleMesh = false;
          break;
        }
        visual = visual.parent;
      }
      if (!isVisibleMesh) continue;
      const joint = this.resolveJoint(intersection.object);
      if (!joint) continue;
      nextJoint = joint;
      this.hitDistance = intersection.distance;
      this.initialGrabPoint.copy(intersection.point);
      break;
    }
    if (nextJoint === this.hovered) return;
    if (this.hovered) this.onUnhover(this.hovered);
    this.hovered = nextJoint;
    if (nextJoint) this.onHover(nextJoint);
  }

  override dispose() {
    this.domElement.removeEventListener('mousedown', this.handleMouseDown);
    this.domElement.removeEventListener('mousemove', this.handleMouseMove);
    this.domElement.removeEventListener('mouseup', this.handleMouseUp);
    this.domElement.removeEventListener('mouseleave', this.handleMouseLeave);
  }
}

export type SelectedWegoJoint = {
  id: string;
  label: string;
  degrees: number;
};

export type WegoJointControl = {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  unit: 'deg' | 'm';
};

export type BodyRotationAxis = 'x' | 'y' | 'z';

const getWegoJointConfig = (jointId: string) =>
  WEGO_JOINTS[jointId as keyof typeof WEGO_JOINTS];

const clampWegoJointDegrees = (jointId: string, degrees: number) => {
  const config = getWegoJointConfig(jointId);
  return config
    ? THREE.MathUtils.clamp(degrees, config.min, config.max)
    : THREE.MathUtils.clamp(degrees, -360, 360);
};

type ConfiguredMotorJoint = {
  id: string;
  joint: WegoJoint;
  config: (typeof WEGO_JOINTS)[keyof typeof WEGO_JOINTS];
};

type SceneOptions = {
  onLoadError?: (message: string) => void;
  onSceneReady?: () => void;
  /** マウス操作で関節を直接動かせるようにする（ジェスチャー作成画面用） */
  jointDragEnabled?: boolean;
  onJointSelectionChange?: (joint: SelectedWegoJoint | null) => void;
  onJointControlsChange?: (joints: WegoJointControl[]) => void;
  onBodyRotationChange?: (rotation: Record<BodyRotationAxis, number>) => void;
  physicsEnabled?: boolean;
  showCollisionBoxes?: boolean;
  tofScanActive?: boolean;
  tofScanDistanceMm?: number;
  mazeStage?: MazeStageDefinition;
  topView?: boolean;
  cameraMode?: MazeCameraMode;
  presentationMode?: boolean;
};

export type { MazeRobotPose } from './mazePose';
export type { MazeCameraMode, MazeCameraRotation } from './mazeCamera';

const createTofPyramidGeometry = () => {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    0, 0, 0,
    0, 0, 0,
    0, 0, 0,
    0, 0, 0,
    0, 0, 0,
  ], 3));
  geometry.setIndex([
    0, 1, 2,
    0, 2, 3,
    0, 3, 4,
    0, 4, 1,
    1, 4, 3,
    1, 3, 2,
  ]);
  geometry.computeVertexNormals();
  return geometry;
};

const EYE_NAMES = /NEW_WEGO_EYES\.(001|002)$/i;
const BACK_LED_NAMES = /^NEW_WEGO_BACK_LED_FILTER\.(001|003)$/i;
const BACK_LED_COLOR = '#038603';
export const SCENE_LOAD_TIMEOUT_MS = 20_000;
/** 影マップを何回に1回更新するか。1なら毎フレーム（従来動作）。 */
const SHADOW_UPDATE_INTERVAL = 3;
const BODY_HIGHLIGHT_JOINTS = new Set(['HEAD_CAB', 'USB_COVER', 'HEAD_BUTTON']);
const ROTATION_HANDLE_AXES = ['X', 'Y', 'Z'] as const;
const TOF_SENSOR_NAME = 'SENSER_PANEL.001';
const TOF_WORLD_DISTANCE = 50 / 20;
const TOF_WORLD_TO_MM = 1000;
/** 目の色適用で使い回すインスタンス。毎ポーズの new を避ける。 */
const eyeColorScratch = new THREE.Color();
/** 姿勢サムネイル（PNG）の一辺のピクセル数。 */
const SNAPSHOT_SIZE = 192;
/** サムネイルは画面の視点に依らず、正面（-Y 方向）固定・距離 1 で撮影する。 */
const SNAPSHOT_CAMERA_DISTANCE = 2;
const SNAPSHOT_CAMERA_FOV = 42;
/** レンダーターゲットはリニア色空間で読み出されるため、sRGB へ戻す変換表。 */
const LINEAR_TO_SRGB_BYTE = (() => {
  const table = new Uint8Array(256);
  for (let value = 0; value < 256; value += 1) {
    const linear = value / 255;
    const srgb = linear <= 0.0031308 ? linear * 12.92 : 1.055 * linear ** (1 / 2.4) - 0.055;
    table[value] = Math.round(Math.min(1, Math.max(0, srgb)) * 255);
  }
  return table;
})();

export const addRotationHandleArrows = (transformHelper: THREE.Object3D) => {
  const axisPlanes: Record<(typeof ROTATION_HANDLE_AXES)[number], [THREE.Vector3, THREE.Vector3]> = {
    X: [new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)],
    Y: [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 1)],
    Z: [new THREE.Vector3(0, 1, 0), new THREE.Vector3(1, 0, 0)],
  };
  const outlineMaterial = new THREE.LineBasicMaterial({
    color: 0xffffff,
    depthTest: false,
    transparent: true,
    opacity: 0.95,
  });

  transformHelper.traverse((object) => {
    const handle = object as THREE.Mesh;
    if (!handle.isMesh || !ROTATION_HANDLE_AXES.includes(handle.name as (typeof ROTATION_HANDLE_AXES)[number])) return;
    if (!(handle.geometry instanceof THREE.TorusGeometry) || handle.geometry.parameters.tube > 0.02) return;
    const axis = handle.name as (typeof ROTATION_HANDLE_AXES)[number];
    const [radialU, radialV] = axisPlanes[axis];
    const axisNormal = radialU.clone().cross(radialV).normalize();
    const positions = handle.geometry.getAttribute('position');
    const point = new THREE.Vector3();
    const centerline = new THREE.Vector3();
    const radius = handle.geometry.parameters.radius;
    const tubeScale = 0.016 / handle.geometry.parameters.tube;
    for (let index = 0; index < positions.count; index += 1) {
      point.fromBufferAttribute(positions, index);
      centerline.copy(point).addScaledVector(axisNormal, -point.dot(axisNormal)).normalize().multiplyScalar(radius);
      point.sub(centerline).multiplyScalar(tubeScale).add(centerline);
      positions.setXYZ(index, point.x, point.y, point.z);
    }
    positions.needsUpdate = true;
    handle.geometry.computeBoundingSphere();
    const fillPositions: number[] = [];
    const outlinePositions: number[] = [];

    [Math.PI / 3, Math.PI * 2 / 3].forEach((angle) => {
      const radial = radialU.clone().multiplyScalar(Math.cos(angle)).addScaledVector(radialV, Math.sin(angle));
      const tangent = radialU.clone().multiplyScalar(-Math.sin(angle)).addScaledVector(radialV, Math.cos(angle));
      const center = radial.multiplyScalar(0.5);
      const tip = center.clone().addScaledVector(tangent, 0.13);
      const tail = center.clone().addScaledVector(tangent, -0.095);
      const left = tail.clone().addScaledVector(radial, 0.085);
      const right = tail.clone().addScaledVector(radial, -0.085);
      [tip, left, right, tip, right, left].forEach((point) => fillPositions.push(point.x, point.y, point.z));
      [[tip, left], [left, right], [right, tip]].forEach(([from, to]) => {
        outlinePositions.push(from.x, from.y, from.z, to.x, to.y, to.z);
      });
    });

    const fillGeometry = new THREE.BufferGeometry();
    fillGeometry.setAttribute('position', new THREE.Float32BufferAttribute(fillPositions, 3));
    const arrows = new THREE.Mesh(fillGeometry, handle.material);
    arrows.raycast = () => undefined;
    arrows.renderOrder = Infinity;
    handle.add(arrows);

    const outlineGeometry = new THREE.BufferGeometry();
    outlineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(outlinePositions, 3));
    const outlines = new THREE.LineSegments(outlineGeometry, outlineMaterial);
    outlines.raycast = () => undefined;
    outlines.renderOrder = Infinity;
    handle.add(outlines);
  });
};

const isIPadDevice = () => (
  /iPad/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

const belongsToNamedObject = (object: THREE.Object3D, names: RegExp) => {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (names.test(current.name)) return true;
    current = current.parent;
  }
  return false;
};

const belongsToCollisionObject = (object: THREE.Object3D) => {
  let current: THREE.Object3D | null = object;
  while (current) {
    if ((current as { isURDFCollider?: boolean }).isURDFCollider) return true;
    current = current.parent;
  }
  return false;
};

const createRobotMaterial = (source?: THREE.Material) => {
  const standardMaterial = source as THREE.MeshStandardMaterial | undefined;
  const sourceColor = standardMaterial?.color ?? new THREE.Color(0x51bbfc);
  if (source?.name === 'metal') {
    return new THREE.MeshPhysicalMaterial({
      color: sourceColor.clone().lerp(new THREE.Color(0xffffff), 0.15),
      metalness: 0.9,
      roughness: 0.05,
      name: source.name,
    });
  }
  return new THREE.MeshStandardMaterial({
    color: sourceColor,
    metalness: 0.6,
    roughness: 0.7,
    name: source?.name,
  });
};

const createGlassEyeMaterial = (source?: THREE.Material) => {
  const standardMaterial = source as THREE.MeshStandardMaterial | undefined;
  const color = standardMaterial?.color ?? new THREE.Color(0xc8f7ff);
  const material = new THREE.MeshPhysicalMaterial({
    color,
    emissive: color,
    emissiveIntensity: 2.6,
    metalness: 0,
    roughness: 0.08,
    transmission: 0,
    thickness: 0.14,
    ior: 1.45,
    transparent: false,
    opacity: 1,
    depthWrite: true,
    reflectivity: 0.35,
    clearcoat: 1,
    clearcoatRoughness: 0.015,
    name: source?.name ?? 'NEW_WEGO_EYES_neon_acrylic',
  });
  material.toneMapped = true;
  return material;
};

const applyEyeLedMaterialState = (material: THREE.MeshStandardMaterial, color: THREE.Color) => {
  const state = getEyeLedSurfaceState(`#${color.getHexString()}`);
  const wasOff = material.userData.filoEyeLedOff;
  const physicalMaterial = material as THREE.MeshPhysicalMaterial;
  material.color.set(state.color);
  material.emissive.set(state.emissive);
  material.emissiveIntensity = state.emissiveIntensity;
  material.transparent = state.transparent;
  material.opacity = state.opacity;
  physicalMaterial.transmission = state.transmission;
  material.roughness = state.roughness;
  material.depthWrite = state.depthWrite;
  material.userData.filoEyeLedOff = state.isOff;
  if (wasOff !== state.isOff) material.needsUpdate = true;
};

const disposeObject = (object: THREE.Object3D) => {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    mesh.geometry?.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    materials.forEach((material) => material.dispose());
  });
};

const createMazeScene = (stage: MazeStageDefinition) => {
  const group = new THREE.Group();
  group.name = 'FILO_MAZE_STAGE';
  const wallGeometry = new THREE.BoxGeometry(0.96, 0.96, 0.55);
  const floorGeometry = new THREE.BoxGeometry(0.94, 0.94, 0.04);
  const wallMaterial = new THREE.MeshStandardMaterial({ color: 0x526578, roughness: 0.72 });
  const floorMaterial = new THREE.MeshStandardMaterial({ color: 0xe7edf3, roughness: 0.9 });
  const startMaterial = new THREE.MeshStandardMaterial({ color: 0x22c55e, roughness: 0.75 });
  const goalMaterial = new THREE.MeshStandardMaterial({ color: 0xef4444, emissive: 0x5b1010, roughness: 0.65 });

  stage.grid.forEach((row, y) => {
    [...row].forEach((cell, x) => {
      const world = mazeGridToWorld(stage, x, y);
      if (cell === '#') {
        const wall = new THREE.Mesh(wallGeometry, wallMaterial);
        wall.name = 'FILO_MAZE_WALL';
        wall.position.set(world.x, world.y, 0.275);
        group.add(wall);
        return;
      }
      const material = cell === 'S' ? startMaterial : cell === 'G' ? goalMaterial : floorMaterial;
      const floor = new THREE.Mesh(floorGeometry, material);
      floor.position.set(world.x, world.y, -0.02);
      group.add(floor);
    });
  });
  return group;
};

const eyeColorAt = (keys: EyeLedKeyframe[], frame: number) => {
  if (keys.length === 0) return DEFAULT_EYE_LED_COLOR;
  for (let index = keys.length - 1; index >= 0; index -= 1) {
    if (keys[index].frame <= frame) return keys[index].color;
  }
  return keys[0].color;
};

export function useWegoAnimationScene({
  onLoadError,
  onSceneReady,
  jointDragEnabled = false,
  onJointSelectionChange,
  onJointControlsChange,
  onBodyRotationChange,
  physicsEnabled = false,
  showCollisionBoxes = false,
  tofScanActive = false,
  tofScanDistanceMm = 0,
  mazeStage,
  topView = false,
  cameraMode = 'free',
  presentationMode = false,
}: SceneOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const robotRef = useRef<WegoRobot | null>(null);
  const robotRootRef = useRef<THREE.Object3D | null>(null);
  const bodyBaseRotationRef = useRef<THREE.Euler | null>(null);
  const bodyEditorRotationRef = useRef<Record<BodyRotationAxis, number>>({ x: 0, y: 0, z: 0 });
  const compiledRef = useRef<CompiledTimeline | null>(null);
  compiledRef.current ??= compileTimeline(createStandbyTimeline());
  const mazeMovementOriginRef = useRef<Record<'x' | 'y' | 'z' | 'yaw', number> | null>(null);
  const lastFrameRef = useRef(-1);
  const lastEyeColorRef = useRef<string | null>(null);
  const eyeMaterialsRef = useRef<THREE.MeshStandardMaterial[]>([]);
  const bloomMaterialsRef = useRef<THREE.MeshStandardMaterial[]>([]);
  const eyeLightsRef = useRef<ForwardEyeLight[]>([]);
  const pausedRef = useRef(false);
  const robotBoundsRef = useRef(new THREE.Box3());
  const renderRef = useRef<((forceShadow?: boolean) => void) | null>(null);
  const updatePreviewFollowRef = useRef<(() => void) | null>(null);
  const captureSnapshotRef = useRef<(() => string | null) | null>(null);
  const selectedJointRef = useRef<SelectableJoint | null>(null);
  const commandedJointDegreesRef = useRef<Record<string, number>>({});
  const physicsWorldRef = useRef<AmmoPhysicsWorld | null>(null);
  const physicsFrameRef = useRef<number | null>(null);
  const physicsEnabledRef = useRef(physicsEnabled);
  const physicsLifecycleRef = useRef<{ start: () => void; stop: () => void } | null>(null);
  const showCollisionBoxesRef = useRef(showCollisionBoxes);
  const tofScanActiveRef = useRef(tofScanActive);
  const tofScanDistanceMmRef = useRef(tofScanDistanceMm);
  const measureTofDistanceMmRef = useRef<(() => number) | null>(null);
  const setSliderHighlightedJointRef = useRef<((jointId: string) => void) | null>(null);
  const mazePoseRef = useRef<MazeRobotPose | null>(mazeStage?.start ?? null);
  const setMazeRobotPoseRef = useRef<((pose: MazeRobotPose, preserveWorldPosition?: boolean) => void) | null>(null);
  const cameraModeRef = useRef<MazeCameraMode>(cameraMode);
  const setMazeCameraModeRef = useRef<((mode: MazeCameraMode) => void) | null>(null);
  const mazeCameraRotationRef = useRef<MazeCameraRotation>({ horizontal: 0, vertical: 0 });
  const setMazeCameraRotationRef = useRef<((rotation: MazeCameraRotation) => void) | null>(null);
  showCollisionBoxesRef.current = showCollisionBoxes;
  physicsEnabledRef.current = physicsEnabled;
  tofScanActiveRef.current = tofScanActive;
  tofScanDistanceMmRef.current = tofScanDistanceMm;
  cameraModeRef.current = cameraMode;

  useEffect(() => {
    if (physicsEnabled) physicsLifecycleRef.current?.start();
    else physicsLifecycleRef.current?.stop();
  }, [physicsEnabled]);

  useEffect(() => {
    physicsWorldRef.current?.setDebugVisible(showCollisionBoxes);
  }, [showCollisionBoxes]);

  useEffect(() => {
    return startTofScanRendering(tofScanActive, () => renderRef.current?.());
  }, [tofScanActive, tofScanDistanceMm]);

  const clampRobotToGround = useCallback(() => {
    const robot = robotRef.current;
    const root = robotRootRef.current;
    if (!robot || !root) return;
    root.updateWorldMatrix(true, false);
    const bounds = robotBoundsRef.current.setFromObject(robot);
    if (bounds.isEmpty() || !Number.isFinite(bounds.min.z)) return;
    const offset = -bounds.min.z;
    if (Math.abs(offset) > 1e-6) {
      robot.position.z += offset / Math.max(1e-6, root.scale.z);
    }
  }, []);

  const setSelectedJointDegrees = useCallback((degrees: number) => {
    const joint = selectedJointRef.current;
    const config = joint ? getWegoJointConfig(joint.name) : undefined;
    if (!joint || !config?.isMotor || !Number.isFinite(degrees)) return;
    const clampedDegrees = Math.min(config.max, Math.max(config.min, degrees));
    commandedJointDegreesRef.current[joint.name] = clampedDegrees;
    joint.setJointValue?.(THREE.MathUtils.degToRad(clampedDegrees));
    physicsWorldRef.current?.setJointTargets({ [joint.name]: clampedDegrees }, 1 / 60);
    if (!physicsEnabledRef.current) clampRobotToGround();
    onJointSelectionChange?.({
      id: joint.name,
      label: config.label,
      degrees: clampedDegrees,
    });
    renderRef.current?.();
  }, [clampRobotToGround, onJointSelectionChange]);

  const getJointControls = useCallback((): WegoJointControl[] => {
    const joints = robotRef.current?.joints ?? {};
    return Object.entries(joints)
      .reduce<ConfiguredMotorJoint[]>((motorJoints, [jointId, joint]) => {
        const config = getWegoJointConfig(jointId);
        if (joint && joint.jointType !== 'fixed' && config?.isMotor) {
          motorJoints.push({ id: jointId, joint, config });
        }
        return motorJoints;
      }, [])
      .sort((first, second) => first.config.index - second.config.index)
      .map(({ id, joint, config }) => {
        const radians = joint.angle ?? joint.jointValue?.[0] ?? 0;
        return {
          id,
          label: config.label,
          value: commandedJointDegreesRef.current[id] ?? THREE.MathUtils.radToDeg(radians),
          min: config.min,
          max: config.max,
          unit: 'deg',
        };
      });
  }, []);

  const publishJointControls = useCallback(() => {
    onJointControlsChange?.(getJointControls());
  }, [getJointControls, onJointControlsChange]);

  const setJointValue = useCallback((jointId: string, value: number) => {
    const joint = robotRef.current?.joints?.[jointId];
    const config = getWegoJointConfig(jointId);
    if (!joint || !config?.isMotor || !Number.isFinite(value)) return;
    const clampedDegrees = clampWegoJointDegrees(jointId, value);
    const nextValue = THREE.MathUtils.degToRad(clampedDegrees);
    commandedJointDegreesRef.current[jointId] = clampedDegrees;
    setSliderHighlightedJointRef.current?.(jointId);
    robotRef.current?.setJointValue?.(jointId, nextValue);
    physicsWorldRef.current?.setJointTargets({ [jointId]: clampedDegrees }, 1 / 60);
    if (!physicsEnabledRef.current) clampRobotToGround();
    publishJointControls();
    if (selectedJointRef.current?.name === jointId) {
      onJointSelectionChange?.({ id: jointId, label: config.label, degrees: clampedDegrees });
    }
    renderRef.current?.();
  }, [clampRobotToGround, onJointSelectionChange, publishJointControls]);

  const setJointPoseValue = useCallback((jointId: string, value: number) => {
    const joint = robotRef.current?.joints?.[jointId];
    const config = getWegoJointConfig(jointId);
    if (!joint || !config?.isMotor || !Number.isFinite(value)) return;
    const clampedDegrees = clampWegoJointDegrees(jointId, value);
    const nextValue = THREE.MathUtils.degToRad(clampedDegrees);
    commandedJointDegreesRef.current[jointId] = clampedDegrees;
    robotRef.current?.setJointValue?.(jointId, nextValue);
    physicsWorldRef.current?.setJointTargets({ [jointId]: clampedDegrees }, 1 / 60);
    if (!physicsEnabledRef.current) clampRobotToGround();
    publishJointControls();
    renderRef.current?.();
  }, [clampRobotToGround, publishJointControls]);

  const setEyeLedColor = useCallback((color: string) => {
    if (!/^#[0-9a-f]{6}$/i.test(color)) return;
    lastEyeColorRef.current = color;
    eyeColorScratch.set(color);
    eyeMaterialsRef.current.forEach((material) => applyEyeLedMaterialState(material, eyeColorScratch));
    eyeLightsRef.current.forEach((light) => {
      light.color.copy(eyeColorScratch);
      light.intensity = getEyeLedIntensity(color, 2.5);
    });
    renderRef.current?.();
  }, []);

  const applyPose = useCallback((frame: number) => {
    const robot = robotRef.current;
    const root = robotRootRef.current;
    const compiled = compiledRef.current;
    if (!robot || !root || !compiled) return;

    const physicsTargets: Record<string, number> = {};
    compiled.joints.forEach((track) => {
      const target = interpolate(track.keyframes, frame);
      const degrees = compiled.data.nativeWalking ? target : clampWegoJointDegrees(track.id, target);
      commandedJointDegreesRef.current[track.id] = degrees;
      robot.setJointValue?.(track.id, THREE.MathUtils.degToRad(degrees));
      physicsTargets[track.id] = degrees;
    });
    physicsWorldRef.current?.setJointTargets(physicsTargets, 1 / 60);

    const movement = (id: MoveTrackId) => {
      const origin = mazeMovementOriginRef.current?.[id as 'x' | 'y' | 'z' | 'yaw'];
      return origin === undefined
        ? interpolate(compiled.movement[id], frame)
        : interpolateFromOrigin(compiled.movement[id], frame, origin);
    };
    const motionY = compiled.movement.y.length ? interpolate(compiled.movement.y, frame) : null;
    if (!physicsEnabledRef.current) {
      root.position.set(movement('x'), movement('z'), motionY ?? 0);
      root.rotation.z = THREE.MathUtils.degToRad(movement('yaw'));
      updatePreviewFollowRef.current?.();
    }

    const body = robot.links?.BODY_link ?? robot.getObjectByName('BODY_link');
    if (body && !physicsEnabledRef.current) {
      bodyBaseRotationRef.current ??= body.rotation.clone();
      const editorRotation = bodyEditorRotationRef.current;
      body.rotation.x = bodyBaseRotationRef.current.x
        + THREE.MathUtils.degToRad(movement('bodyRotationX') + editorRotation.x);
      body.rotation.y = bodyBaseRotationRef.current.y
        + THREE.MathUtils.degToRad(movement('bodyRotationY') + editorRotation.y);
      body.rotation.z = bodyBaseRotationRef.current.z
        + THREE.MathUtils.degToRad(movement('bodyRotationZ') + editorRotation.z);
    }

    const eyeColor = eyeColorAt(compiled.eyeLedKeyframes, frame);
    if (eyeColor !== lastEyeColorRef.current) {
      lastEyeColorRef.current = eyeColor;
      eyeColorScratch.set(eyeColor);
      eyeMaterialsRef.current.forEach((material) => applyEyeLedMaterialState(material, eyeColorScratch));
      eyeLightsRef.current.forEach((light) => {
        light.color.copy(eyeColorScratch);
        light.intensity = getEyeLedIntensity(eyeColor, 2.5);
      });
    }

    if (motionY === null && !physicsEnabledRef.current) clampRobotToGround();
    publishJointControls();
    renderRef.current?.();
  }, [clampRobotToGround, publishJointControls]);

  const setBodyRotation = useCallback((axis: BodyRotationAxis, degrees: number) => {
    if (!Number.isFinite(degrees)) return;
    const body = robotRef.current?.links?.BODY_link ?? robotRef.current?.getObjectByName('BODY_link');
    if (!body) return;
    const nextDegrees = THREE.MathUtils.clamp(degrees, -180, 180);
    const previousDegrees = bodyEditorRotationRef.current[axis];
    bodyEditorRotationRef.current[axis] = nextDegrees;
    body.rotation[axis] += THREE.MathUtils.degToRad(nextDegrees - previousDegrees);
    if (!physicsEnabledRef.current) clampRobotToGround();
    renderRef.current?.();
  }, [clampRobotToGround]);

  const showFrame = useCallback((frame: number) => {
    if (frame === lastFrameRef.current) return;
    lastFrameRef.current = frame;
    if (pausedRef.current) return;
    applyPose(frame);
  }, [applyPose]);

  const loadTimeline = useCallback((timeline: TimelineData) => {
    compiledRef.current = compileTimeline(timeline);
    const root = robotRootRef.current;
    if (timeline.nativeWalking && robotRef.current) {
      robotRef.current.position.set(0, 0, 0);
    }
    mazeMovementOriginRef.current = mazeStage && root
      ? {
        x: root.position.x,
        y: root.position.z,
        z: root.position.y,
        yaw: THREE.MathUtils.radToDeg(root.rotation.z),
      }
      : null;
    lastFrameRef.current = -1;
    lastEyeColorRef.current = null;
    showFrame(0);
    renderRef.current?.(true);
  }, [mazeStage, showFrame]);

  const setPaused = useCallback((paused: boolean) => {
    if (pausedRef.current === paused) return;
    pausedRef.current = paused;
    if (!paused && lastFrameRef.current >= 0) applyPose(lastFrameRef.current);
  }, [applyPose]);

  /** 現在の姿勢を背景透過・グリッド非表示の PNG データ URL として取得する。 */
  const captureSnapshot = useCallback(() => captureSnapshotRef.current?.() ?? null, []);

  const setMazeRobotPose = useCallback((pose: MazeRobotPose, preserveWorldPosition = false) => {
    mazePoseRef.current = pose;
    setMazeRobotPoseRef.current?.(pose, preserveWorldPosition);
  }, []);

  const setMazeCameraMode = useCallback((mode: MazeCameraMode) => {
    cameraModeRef.current = mode;
    setMazeCameraModeRef.current?.(mode);
  }, []);

  const setMazeCameraRotation = useCallback((rotation: MazeCameraRotation) => {
    mazeCameraRotationRef.current = rotation;
    setMazeCameraRotationRef.current?.(rotation);
  }, []);

  const measureTofDistanceMm = useCallback(() => measureTofDistanceMmRef.current?.() ?? 0, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let loadFailed = false;
    let loadCompleted = false;
    let pendingRobot: WegoRobot | null = null;
    let pendingRoot: THREE.Object3D | null = null;
    const mazeAnchor = mazeStage ? new THREE.Object3D() : null;
    let tofPyramid: THREE.Mesh | null = null;
    let tofPyramidEdges: THREE.LineSegments | null = null;
    let sceneLoadTimer: number | null = null;
    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);
    const iPad = isIPadDevice();
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(presentationMode ? 0xffffff : PREVIEW_BACKGROUND_COLOR);

    const camera = new THREE.PerspectiveCamera(42, width / height, 0.01, 100);
    camera.up.set(0, 0, 1);
    camera.position.set(0.8, -2.7, 1.25);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = !iPad;
    renderer.shadowMap.autoUpdate = false;
    container.replaceChildren(renderer.domElement);
    const eyeBloom = new EyeBloom(renderer);

    const pmrem = new THREE.PMREMGenerator(renderer);
    const environment = pmrem.fromScene(new RoomEnvironment(), 0.04);
    scene.environment = environment.texture;
    scene.add(new THREE.HemisphereLight(0xffffff, 0x7890a4, 2.8));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
    keyLight.position.set(-2, -3, 4);
    keyLight.castShadow = !iPad;
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0x9bdcff, 1.1);
    fillLight.position.set(3, -1, 2);
    scene.add(fillLight);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(2.2, 64),
      new THREE.ShadowMaterial({ color: 0x284052, opacity: 0.18, transparent: true }),
    );
    ground.receiveShadow = !iPad;
    if (!iPad && !presentationMode) scene.add(ground);
    const floor = createPreviewFloor();
    if (!mazeStage && !presentationMode) scene.add(floor);
    const mazeGroup = mazeStage ? createMazeScene(mazeStage) : null;
    if (mazeGroup) scene.add(mazeGroup);

    const getTofRay = () => {
      const sensorPanel = robotRef.current?.getObjectByName(TOF_SENSOR_NAME)
        ?? robotRef.current?.getObjectByName('SENSER_PANEL');
      return createTofRay(sensorPanel, mazeAnchor, robotRootRef.current);
    };

    measureTofDistanceMmRef.current = () => {
      const ray = getTofRay();
      if (!ray || !mazeGroup) return 0;
      const collisionOrigin = ray.origin.clone().setZ(0.275);
      const walls = mazeGroup.children.filter((child) => child.name === 'FILO_MAZE_WALL');
      mazeGroup.updateWorldMatrix(true, true);
      const collisionRay = new THREE.Ray(collisionOrigin, ray.direction);
      const hitDistance = walls.reduce((nearest, wall) => {
        const hit = collisionRay.intersectBox(new THREE.Box3().setFromObject(wall), new THREE.Vector3());
        if (!hit) return nearest;
        const distance = collisionOrigin.distanceTo(hit);
        return distance <= TOF_WORLD_DISTANCE ? Math.min(nearest, distance) : nearest;
      }, Number.POSITIVE_INFINITY);
      if (Number.isFinite(hitDistance)) return Math.round(hitDistance * TOF_WORLD_TO_MM);
      return mazeStage && mazePoseRef.current
        ? measureMazeWallDistanceMm(mazeStage, mazePoseRef.current, TOF_WORLD_DISTANCE * TOF_WORLD_TO_MM)
        : 0;
    };

    const updateTofPyramid = () => {
      if (!tofPyramid || !tofPyramidEdges || !robotRef.current) return;
      tofPyramid.visible = tofScanActiveRef.current;
      tofPyramidEdges.visible = tofScanActiveRef.current;
      if (!tofScanActiveRef.current) return;

      const ray = getTofRay();
      if (!ray) return;
      const { origin, direction } = ray;
      const detectedDistance = tofScanDistanceMmRef.current / TOF_WORLD_TO_MM;
      const scanDistance = detectedDistance > 0
        ? Math.min(detectedDistance, TOF_WORLD_DISTANCE)
        : TOF_WORLD_DISTANCE;
      const corners = createTofBaseCorners(origin, direction, scanDistance);
      const position = tofPyramid.geometry.getAttribute('position') as THREE.BufferAttribute;
      position.setXYZ(0, origin.x, origin.y, origin.z);
      corners.forEach((corner, index) => position.setXYZ(index + 1, corner.x, corner.y, corner.z));
      position.needsUpdate = true;
      tofPyramid.geometry.computeVertexNormals();
      tofPyramid.geometry.computeBoundingSphere();
      const edgePosition = tofPyramidEdges.geometry.getAttribute('position') as THREE.BufferAttribute;
      edgePosition.setXYZ(0, origin.x, origin.y, origin.z);
      corners.forEach((corner, index) => edgePosition.setXYZ(index + 1, corner.x, corner.y, corner.z));
      edgePosition.needsUpdate = true;
      tofPyramidEdges.geometry.computeBoundingSphere();
    };

    let updateRobotViewCamera = () => undefined;
    let shadowTick = 0;
    const render = (forceShadow = false) => {
      shadowTick = (shadowTick + 1) % SHADOW_UPDATE_INTERVAL;
      renderer.shadowMap.needsUpdate = forceShadow || shadowTick === 0;
      updateRobotViewCamera();
      updateTofPyramid();
      eyeBloom.render(scene, camera, bloomMaterialsRef.current);
    };
    let snapshotRenderTarget: THREE.WebGLRenderTarget | null = null;
    let snapshotCanvas: HTMLCanvasElement | null = null;
    const snapshotFocus = new THREE.Vector3();
    const snapshotCamera = new THREE.PerspectiveCamera(SNAPSHOT_CAMERA_FOV, 1, 0.01, 100);
    snapshotCamera.up.set(0, 0, 1);
    const takeSnapshot = (): string | null => {
      if (!robotRef.current) return null;
      snapshotRenderTarget ??= new THREE.WebGLRenderTarget(SNAPSHOT_SIZE, SNAPSHOT_SIZE, { stencilBuffer: false });
      if (!snapshotCanvas) {
        snapshotCanvas = document.createElement('canvas');
        snapshotCanvas.width = SNAPSHOT_SIZE;
        snapshotCanvas.height = SNAPSHOT_SIZE;
      }
      const context = snapshotCanvas.getContext('2d');
      if (!context) return null;
      snapshotCamera.position.set(snapshotFocus.x, snapshotFocus.y - SNAPSHOT_CAMERA_DISTANCE, snapshotFocus.z);
      snapshotCamera.lookAt(snapshotFocus);
      snapshotCamera.updateMatrixWorld(true);
      const previousBackground = scene.background;
      const previousFloorVisible = floor.visible;
      const previousGroundVisible = ground.visible;
      const previousClearAlpha = renderer.getClearAlpha();
      const previousTransformHelperVisible = transformHelper.visible;
      const highlightedJoints = [...new Set([hoveredJoint, sliderHighlightedJoint].filter(
        (joint): joint is SelectableJoint => joint !== null,
      ))];
      scene.background = null;
      floor.visible = false;
      ground.visible = false;
      transformHelper.visible = false;
      highlightedJoints.forEach((joint) => highlightJoint(joint, true));
      renderer.setClearAlpha(0);
      renderer.setRenderTarget(snapshotRenderTarget);
      renderer.clear();
      eyeBloom.render(scene, snapshotCamera, bloomMaterialsRef.current, snapshotRenderTarget);
      const pixels = new Uint8Array(SNAPSHOT_SIZE * SNAPSHOT_SIZE * 4);
      renderer.readRenderTargetPixels(snapshotRenderTarget, 0, 0, SNAPSHOT_SIZE, SNAPSHOT_SIZE, pixels);
      renderer.setRenderTarget(null);
      renderer.setClearAlpha(previousClearAlpha);
      scene.background = previousBackground;
      floor.visible = previousFloorVisible;
      ground.visible = previousGroundVisible;
      transformHelper.visible = previousTransformHelperVisible;
      highlightedJoints.forEach((joint) => highlightJoint(joint, false));
      render(true);
      const image = context.createImageData(SNAPSHOT_SIZE, SNAPSHOT_SIZE);
      const rowBytes = SNAPSHOT_SIZE * 4;
      for (let row = 0; row < SNAPSHOT_SIZE; row += 1) {
        // readRenderTargetPixels は下の行から返るため、上下を反転して書き戻す。
        const source = (SNAPSHOT_SIZE - 1 - row) * rowBytes;
        const destination = row * rowBytes;
        for (let offset = 0; offset < rowBytes; offset += 4) {
          const alpha = pixels[source + offset + 3];
          // Bloom is blended as premultiplied light; PNG ImageData expects straight alpha.
          const unpremultiply = alpha > 0 ? 255 / alpha : 0;
          for (let channel = 0; channel < 3; channel += 1) {
            const linear = Math.min(255, Math.round(pixels[source + offset + channel] * unpremultiply));
            image.data[destination + offset + channel] = LINEAR_TO_SRGB_BYTE[linear];
          }
          image.data[destination + offset + 3] = alpha;
        }
      }
      context.putImageData(image, 0, 0);
      return snapshotCanvas.toDataURL('image/png');
    };
    captureSnapshotRef.current = takeSnapshot;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;
    controls.rotateSpeed = 0.75;
    controls.zoomSpeed = 0.8;
    controls.minPolarAngle = THREE.MathUtils.degToRad(6);
    controls.maxPolarAngle = THREE.MathUtils.degToRad(88);
    const previewFollowOffset = new THREE.Vector3();
    const updatePreviewFollow = () => {
      const root = robotRootRef.current;
      if (!root || mazeStage) return;
      followPreviewMovement({
        robotPosition: root.position,
        previousOffset: previewFollowOffset,
        cameraPosition: camera.position,
        cameraTarget: controls.target,
        groundPosition: ground.position,
        floorPosition: floor.position,
        floorTextureOffset: floor.material.map!.offset,
        floorTextureRepeat: floor.material.map!.repeat,
        floorSize: PREVIEW_FLOOR_SIZE,
      });
    };
    updatePreviewFollowRef.current = updatePreviewFollow;
    const fitMazeCamera = () => {
      if (!mazeStage) return;
        const aspect = Math.max(0.2, camera.aspect);
        const visibleHeight = Math.max(mazeStage.grid.length, (mazeStage.grid[0]?.length ?? 1) / aspect) + 1.5;
        const distance = visibleHeight / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
        const freeCamera = getMazeFreeCameraRotation(distance, mazeCameraRotationRef.current);
        camera.up.copy(freeCamera.up);
        camera.position.copy(freeCamera.positionOffset);
        camera.lookAt(0, 0, 0);
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);
        controls.enableRotate = MAZE_FREE_CAMERA_CONTROLS.rotate;
        controls.enablePan = MAZE_FREE_CAMERA_CONTROLS.pan;
        controls.enableZoom = MAZE_FREE_CAMERA_CONTROLS.zoom;
        controls.screenSpacePanning = true;
        controls.minDistance = 3;
        controls.maxDistance = visibleHeight * 1.5;
        controls.update();
    };
    let robotCameraBaselineCenter: THREE.Vector3 | null = null;
    let robotCameraBasePose: ReturnType<typeof getRobotViewCameraPose> | null = null;
    const getRobotBodyCenter = () => {
      const body = robotRef.current?.links?.BODY_link ?? robotRef.current?.getObjectByName('BODY_link');
      if (!body) return null;
      body.updateWorldMatrix(true, false);
      return body.getWorldPosition(new THREE.Vector3());
    };
    const followRobotCamera = (pose: MazeRobotPose) => {
      if (!mazeStage) return;
      const world = mazeGridToWorld(mazeStage, pose.x, pose.y);
      robotCameraBasePose = getRobotViewCameraPose({ worldX: world.x, worldY: world.y, dir: pose.dir });
      robotCameraBaselineCenter = getRobotBodyCenter();
      const { position, target } = robotCameraBasePose;
      camera.up.set(0, 0, 1);
      camera.position.copy(position);
      camera.lookAt(target);
      controls.target.copy(target);
      controls.update();
      renderRef.current?.();
    };
    updateRobotViewCamera = () => {
      if (cameraModeRef.current !== 'robot' || !robotCameraBasePose || !robotCameraBaselineCenter) return;
      const currentCenter = getRobotBodyCenter();
      if (!currentCenter) return;
      const { position, target } = offsetRobotViewCameraPose(
        robotCameraBasePose,
        robotCameraBaselineCenter,
        currentCenter,
      );
      camera.position.copy(position);
      camera.lookAt(target);
      controls.target.copy(target);
    };
    const applyMazeCameraMode = (mode: MazeCameraMode) => {
      cameraModeRef.current = mode;
      controls.enabled = mode === 'free';
      if (mode === 'robot' && mazePoseRef.current) followRobotCamera(mazePoseRef.current);
      else if (mazeStage && topView) fitMazeCamera();
    };
    setMazeCameraModeRef.current = applyMazeCameraMode;
    setMazeCameraRotationRef.current = (rotation) => {
      mazeCameraRotationRef.current = rotation;
      if (cameraModeRef.current === 'free') fitMazeCamera();
    };
    if (mazeStage && topView) {
      fitMazeCamera();
      applyMazeCameraMode(cameraModeRef.current);
    }
    const handleControlsChange = () => render();
    const transformControls = new TransformControls(camera, renderer.domElement);
    transformControls.setMode('rotate');
    transformControls.setSpace('local');
    transformControls.setSize(0.75);
    const transformHelper = transformControls.getHelper();
    addRotationHandleArrows(transformHelper);
    transformHelper.visible = false;
    scene.add(transformHelper);
    let transformStartRotation = new THREE.Euler();
    let transformStartEditorRotation: Record<BodyRotationAxis, number> = { x: 0, y: 0, z: 0 };
    transformControls.addEventListener('mouseDown', () => {
      if (!transformControls.object) return;
      transformStartRotation = transformControls.object.rotation.clone();
      transformStartEditorRotation = { ...bodyEditorRotationRef.current };
    });
    transformControls.addEventListener('objectChange', () => {
      const body = transformControls.object;
      const axis = transformControls.axis?.toLowerCase() as BodyRotationAxis | undefined;
      if (body?.name !== 'BODY_link' || !axis || !(axis in bodyEditorRotationRef.current)) return;
      const delta = THREE.MathUtils.radToDeg(
        THREE.MathUtils.euclideanModulo(body.rotation[axis] - transformStartRotation[axis] + Math.PI, Math.PI * 2) - Math.PI,
      );
      const clampedDegrees = THREE.MathUtils.clamp(transformStartEditorRotation[axis] + delta, -180, 180);
      bodyEditorRotationRef.current[axis] = Math.round(clampedDegrees * 10) / 10;
      onBodyRotationChange?.({ ...bodyEditorRotationRef.current });
      if (!physicsEnabledRef.current) clampRobotToGround();
      render();
    });
    transformControls.addEventListener('dragging-changed', (event) => {
      controls.enabled = !event.value;
    });
    controls.addEventListener('change', handleControlsChange);

    const fitCamera = (root: THREE.Object3D) => {
      if (mazeStage && topView) return;
      root.updateMatrixWorld(true);
      const bounds = new THREE.Box3().setFromObject(root);
      if (bounds.isEmpty()) return;
      const center = bounds.getCenter(new THREE.Vector3());
      const size = bounds.getSize(new THREE.Vector3());
      snapshotFocus.copy(center);
      const distance = Math.max(1.4, Math.max(size.x, size.y, size.z) * 2.3);
      camera.position.set(center.x + distance * 0.28, center.y - distance, center.z + distance * 0.34);
      camera.near = Math.max(0.01, distance / 100);
      camera.far = distance * 100;
      camera.updateProjectionMatrix();
      controls.target.copy(center);
      controls.minDistance = distance * 0.45;
      controls.maxDistance = distance * 3.5;
      controls.update();
    };

    renderRef.current = render;
    render();

    let dragControls: CanvasPointerURDFDragControls | null = null;
    let hoveredJoint: SelectableJoint | null = null;
    let sliderHighlightedJoint: SelectableJoint | null = null;
    const highlightMaterial = new THREE.MeshPhongMaterial({
      shininess: 10,
      color: 0xffcaca,
      emissive: 0xffcaca,
      emissiveIntensity: 0.12,
      flatShading: true,
    });
    
    const isMovableJoint = (object: THREE.Object3D) => {
      const joint = object as SelectableJoint;
      return (joint as { isURDFJoint?: boolean }).isURDFJoint && joint.jointType !== 'fixed';
    };
    // urdf-manipulator と同じく、次の可動関節の手前までにあるリンク形状だけを置き換える。
    const highlightJoint = (target: THREE.Object3D, revert: boolean) => {
      const traverse = (child: THREE.Object3D) => {
        const mesh = child as HighlightableMesh;
        if (mesh.isMesh) {
          if (revert) {
            if (mesh.originalMaterial) {
              mesh.material = mesh.originalMaterial;
              mesh.originalMaterial = undefined;
            }
          } else if (mesh.material !== highlightMaterial) {
            mesh.originalMaterial = mesh.material;
            mesh.material = highlightMaterial;
          }
        }
        const isBodyAccessory = target.name === 'BODY_link' && BODY_HIGHLIGHT_JOINTS.has(child.name);
        if (child === target || !isMovableJoint(child) || isBodyAccessory) {
          child.children.forEach((nextChild) => {
            if (!(nextChild as { isURDFCollider?: boolean }).isURDFCollider) traverse(nextChild);
          });
        }
      };
      traverse(target);
    };
    const setSliderHighlightedJoint = (jointId: string) => {
      const nextJoint = robotRef.current?.joints?.[jointId] as SelectableJoint | undefined;
      if (!nextJoint || !getWegoJointConfig(jointId)?.isMotor || nextJoint === sliderHighlightedJoint) return;
      if (sliderHighlightedJoint && sliderHighlightedJoint !== hoveredJoint) {
        highlightJoint(sliderHighlightedJoint, true);
      }
      sliderHighlightedJoint = nextJoint;
      highlightJoint(sliderHighlightedJoint, false);
      render(true);
    };
    const clearSliderHighlightedJoint = () => {
      if (!sliderHighlightedJoint) return;
      if (sliderHighlightedJoint !== hoveredJoint) highlightJoint(sliderHighlightedJoint, true);
      sliderHighlightedJoint = null;
      render(true);
    };
    setSliderHighlightedJointRef.current = setSliderHighlightedJoint;
    const getJointDetails = (joint: SelectableJoint): SelectedWegoJoint => ({
      id: joint.name,
      label: joint.name === 'BODY_link' ? 'Body' : joint.name,
      degrees: THREE.MathUtils.radToDeg(joint.angle ?? joint.jointValue?.[0] ?? 0),
    });
    const selectJoint = (joint: SelectableJoint | null) => {
      selectedJointRef.current = joint;
      if (joint?.name === 'BODY_link' && !physicsEnabledRef.current) {
        transformControls.enabled = true;
        transformControls.showX = true;
        transformControls.showY = true;
        transformControls.showZ = true;
        transformControls.attach(joint);
        transformHelper.visible = true;
      } else if (joint && getWegoJointConfig(joint.name)?.isMotor && !physicsEnabledRef.current) {
        transformControls.enabled = false;
        transformControls.showX = Math.abs(joint.axis?.x ?? 0) > 0.001;
        transformControls.showY = Math.abs(joint.axis?.y ?? 0) > 0.001;
        transformControls.showZ = Math.abs(joint.axis?.z ?? 0) > 0.001;
        transformControls.attach(joint);
        transformHelper.visible = true;
      } else {
        transformControls.enabled = true;
        transformControls.detach();
        transformHelper.visible = false;
      }
      render(true);
      onJointSelectionChange?.(joint ? getJointDetails(joint) : null);
    };
    const handleSelectionMouseDown = () => {
      if (transformControls.axis) return;
      // PointerURDFDragControls の mousedown 後には hovered が最新の可動関節になる。
      selectJoint(dragControls?.hovered as SelectableJoint | null ?? null);
    };
    const createDragControls = (robot: WegoRobot) => {
      // scene 全体ではなくロボットだけを対象にする。地面・グリッドが先に当たると関節を選べないため。
      const dragging = new CanvasPointerURDFDragControls(
        robot,
        camera,
        renderer.domElement,
        (object) => {
          let candidate: THREE.Object3D | null = object;
          while (candidate) {
            if ((candidate as { isURDFJoint?: boolean }).isURDFJoint
              && getWegoJointConfig(candidate.name)?.isMotor) return candidate;
            if (candidate.name === 'BODY_link') return candidate;
            if (candidate === robot) break;
            candidate = candidate.parent;
          }
          return null;
        },
        (object) => object.name !== 'BODY_link' && selectedJointRef.current?.name !== 'BODY_link',
      );
      let touchDragging = false;
      const dispatchMouseEvent = (type: 'mousedown' | 'mousemove' | 'mouseup', touch: Touch) => {
        renderer.domElement.dispatchEvent(new MouseEvent(type, {
          bubbles: true,
          clientX: touch.clientX,
          clientY: touch.clientY,
          button: 0,
          buttons: type === 'mouseup' ? 0 : 1,
        }));
      };
      const handleTouchStart = (event: TouchEvent) => {
        const touch = event.touches[0];
        if (!touch) return;
        dispatchMouseEvent('mousemove', touch);
        if (!dragging.hovered || !getWegoJointConfig(dragging.hovered.name)?.isMotor) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        touchDragging = true;
        controls.enabled = false;
        dispatchMouseEvent('mousedown', touch);
      };
      const handleTouchMove = (event: TouchEvent) => {
        if (!touchDragging) return;
        const touch = event.touches[0];
        if (!touch) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        dispatchMouseEvent('mousemove', touch);
      };
      const handleTouchEnd = (event: TouchEvent) => {
        if (!touchDragging) return;
        const touch = event.changedTouches[0];
        if (touch) dispatchMouseEvent('mouseup', touch);
        event.preventDefault();
        event.stopImmediatePropagation();
        touchDragging = false;
      };
      dragging.onHover = (joint) => {
        const hovered = joint as SelectableJoint;
        if (hovered.name !== 'BODY_link' && !getWegoJointConfig(hovered.name)?.isMotor) {
          // URDFDragControls はこの値を次の mousedown 時に使うため、非モーター関節を候補から外す。
          dragging.hovered = null;
          return;
        }
        hoveredJoint = hovered;
        highlightJoint(hoveredJoint, false);
        render(true);
      };
      dragging.onUnhover = (joint) => {
        if (joint.name !== 'BODY_link' && !getWegoJointConfig(joint.name)?.isMotor) return;
        if (joint !== sliderHighlightedJoint) highlightJoint(joint, true);
        if (hoveredJoint === joint) hoveredJoint = null;
        render(true);
      };
      dragging.onDragStart = (joint) => {
        const selected = joint as SelectableJoint;
        if (!getWegoJointConfig(selected.name)?.isMotor) return;
        clearSliderHighlightedJoint();
        selectJoint(selected);
        controls.enabled = false;
      };
      dragging.onDragEnd = () => { controls.enabled = true; render(true); };
      dragging.updateJoint = (joint, angle) => {
        const selectedJoint = joint as SelectableJoint;
        const config = getWegoJointConfig(selectedJoint.name);
        if (!config?.isMotor) return;
        if (selectedJointRef.current !== selectedJoint) selectJoint(selectedJoint);
        const degrees = Math.min(config.max, Math.max(config.min, THREE.MathUtils.radToDeg(angle)));
        commandedJointDegreesRef.current[selectedJoint.name] = degrees;
        selectedJoint.setJointValue?.(THREE.MathUtils.degToRad(degrees));
        physicsWorldRef.current?.setJointTargets({ [selectedJoint.name]: degrees }, 1 / 60);
        if (!physicsEnabledRef.current) clampRobotToGround();
        onJointSelectionChange?.({
          ...getJointDetails(selectedJoint),
          label: config.label,
          degrees,
        });
        publishJointControls();
        render();
      };
      renderer.domElement.addEventListener('mousedown', handleSelectionMouseDown);
      if (iPad) {
        renderer.domElement.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
        renderer.domElement.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
        renderer.domElement.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });
        renderer.domElement.addEventListener('touchcancel', handleTouchEnd, { capture: true, passive: false });
      }
      const disposeDragControls = dragging.dispose.bind(dragging);
      dragging.dispose = () => {
        renderer.domElement.removeEventListener('mousedown', handleSelectionMouseDown);
        if (iPad) {
          renderer.domElement.removeEventListener('touchstart', handleTouchStart, true);
          renderer.domElement.removeEventListener('touchmove', handleTouchMove, true);
          renderer.domElement.removeEventListener('touchend', handleTouchEnd, true);
          renderer.domElement.removeEventListener('touchcancel', handleTouchEnd, true);
        }
        disposeDragControls();
      };
      return dragging;
    };

    const clearSceneLoadTimer = () => {
      if (sceneLoadTimer !== null) window.clearTimeout(sceneLoadTimer);
      sceneLoadTimer = null;
    };
    const failSceneLoad = (message: string) => {
      if (disposed || loadFailed || loadCompleted) return;
      loadFailed = true;
      clearSceneLoadTimer();
      onLoadError?.(message);
    };
    sceneLoadTimer = window.setTimeout(() => {
      failSceneLoad('ロボットの3Dモデルを20秒以内に読み込めませんでした。');
    }, SCENE_LOAD_TIMEOUT_MS);
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      onLoadError?.('3D表示が停止しました。ページを再読み込みしてください。');
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    let physicsStarting = false;
    let physicsVisualRoot: THREE.Group | null = null;
    const stopPhysics = () => {
      if (physicsFrameRef.current !== null) {
        window.cancelAnimationFrame(physicsFrameRef.current);
        physicsFrameRef.current = null;
      }
      physicsWorldRef.current?.dispose();
      physicsWorldRef.current = null;
      if (physicsVisualRoot) {
        physicsVisualRoot.parent?.remove(physicsVisualRoot);
        physicsVisualRoot = null;
      }
      if (pendingRobot) pendingRobot.visible = true;
      render(true);
    };
    const startPhysics = async () => {
      if (disposed || physicsStarting || physicsWorldRef.current || !pendingRobot || !pendingRoot) return;
      physicsStarting = true;
      const robot = pendingRobot;
      const root = pendingRoot;
      let physicsWorld: AmmoPhysicsWorld | null = null;
      try {
        physicsWorld = await AmmoPhysicsWorld.create();
        if (disposed || !physicsEnabledRef.current || robotRef.current !== robot) {
          physicsWorld.dispose();
          return;
        }
        physicsWorld.setJointObjects(robot.joints ?? {});
        root.position.set(0, 0, 0);
        root.rotation.set(0, 0, 0);
        const body = robot.links?.BODY_link ?? robot.getObjectByName('BODY_link');
        if (body && bodyBaseRotationRef.current) body.rotation.copy(bodyBaseRotationRef.current);
        const visualRoot = new THREE.Group();
        visualRoot.name = 'wego_physics_visuals';
        root.add(visualRoot);
        physicsVisualRoot = visualRoot;
        robot.updateMatrixWorld(true);
        const visualObjectsById = new Map<string, THREE.Object3D>();
        Object.entries(robot.links ?? {}).forEach(([id, link]) => {
          if (!link || !AmmoPhysicsWorld.shouldIncludeLink(id)) return;
          const bounds = new THREE.Box3().setFromObject(link);
          const size = bounds.getSize(new THREE.Vector3());
          const mass = Number(link.inertial?.mass) || 1;
          const inertialOrigin = link.inertial?.origin?.xyz;
          const centerOfMass = Array.isArray(inertialOrigin) && inertialOrigin.length >= 3
            ? new THREE.Vector3(
              Number(inertialOrigin[0]) || 0,
              Number(inertialOrigin[1]) || 0,
              Number(inertialOrigin[2]) || 0,
            )
            : undefined;
          const visualObject = new THREE.Group();
          visualObject.name = `${id}_physics_visual`;
          link.children.forEach((child) => {
            const node = child as THREE.Object3D & { isURDFVisual?: boolean; isURDFCollider?: boolean };
            if (node.isURDFVisual || node.isURDFCollider) visualObject.add(node.clone(true));
          });
          visualRoot.add(visualObject);
          visualObjectsById.set(id, visualObject);
          const debugObject = visualObject.children.find((child) => (child as { isURDFCollider?: boolean }).isURDFCollider);
          physicsWorld!.addLink({ id, object: link, visualObject, debugObject, size, mass, centerOfMass });
        });
        // 物理演算の対象外リンク（頭部カバーなど）も、非表示にならないよう最寄りの物理リンクへ追従させる。
        Object.entries(robot.links ?? {}).forEach(([id, link]) => {
          if (!link || AmmoPhysicsWorld.shouldIncludeLink(id)) return;
          let ancestor: THREE.Object3D | null = link.parent;
          let ancestorId: string | null = null;
          while (ancestor) {
            if (ancestor.name && visualObjectsById.has(ancestor.name)) {
              ancestorId = ancestor.name;
              break;
            }
            ancestor = ancestor.parent;
          }
          const ancestorLink = ancestorId ? robot.links?.[ancestorId] : undefined;
          const ancestorVisual = ancestorId ? visualObjectsById.get(ancestorId) : undefined;
          if (!ancestorLink || !ancestorVisual) return;
          const followerObject = new THREE.Group();
          followerObject.name = `${id}_physics_visual`;
          link.children.forEach((child) => {
            const node = child as THREE.Object3D & { isURDFVisual?: boolean; isURDFCollider?: boolean };
            if (node.isURDFVisual || node.isURDFCollider) followerObject.add(node.clone(true));
          });
          new THREE.Matrix4()
            .copy(ancestorLink.matrixWorld)
            .invert()
            .multiply(link.matrixWorld)
            .decompose(followerObject.position, followerObject.quaternion, followerObject.scale);
          ancestorVisual.add(followerObject);
        });
        physicsWorld.primeJointTargets(
          Object.fromEntries(
            Object.entries(WEGO_JOINTS)
              .filter(([, joint]) => joint.isMotor)
              .map(([jointId, joint]) => {
                const robotJoint = robot.joints?.[jointId];
                const radians = robotJoint?.angle ?? robotJoint?.jointValue?.[0];
                return [
                  jointId,
                  Number.isFinite(radians)
                    ? clampWegoJointDegrees(jointId, THREE.MathUtils.radToDeg(radians!))
                    : joint.default,
                ];
              }),
          ),
        );
        physicsWorld.setDebugVisible(showCollisionBoxesRef.current);
        physicsWorldRef.current = physicsWorld;
        physicsWorld.syncTargets(root);
        robot.visible = false;
        render(true);
        let previousTime = performance.now();
        const activePhysicsWorld = physicsWorld;
        const runPhysics = (time: number) => {
          if (disposed || physicsWorldRef.current !== activePhysicsWorld) return;
          const deltaSeconds = Math.min(1 / 20, Math.max(0, (time - previousTime) / 1000));
          previousTime = time;
          activePhysicsWorld.step(deltaSeconds || 1 / 60);
          activePhysicsWorld.syncTargets(root);
          const diagnostics = activePhysicsWorld.getDiagnostics();
          if (diagnostics) {
            renderer.domElement.dataset.physicsTiltDegrees = diagnostics.tiltDegrees.toFixed(2);
            renderer.domElement.dataset.physicsDisplacement = diagnostics.displacement.toFixed(4);
            renderer.domElement.dataset.physicsHeightDelta = diagnostics.heightDelta.toFixed(4);
          }
          renderRef.current?.();
          physicsFrameRef.current = window.requestAnimationFrame(runPhysics);
        };
        physicsFrameRef.current = window.requestAnimationFrame(runPhysics);
      } catch (error: unknown) {
        physicsWorld?.dispose();
        if (physicsVisualRoot) {
          physicsVisualRoot.parent?.remove(physicsVisualRoot);
          physicsVisualRoot = null;
        }
        robot.visible = true;
        const message = error instanceof Error ? error.message : String(error);
        console.error(`物理エンジンの初期化に失敗しました: ${message}`);
        onLoadError?.(`物理エンジンを初期化できませんでした: ${message}`);
      } finally {
        physicsStarting = false;
      }
    };
    const physicsLifecycle = { start: () => { void startPhysics(); }, stop: stopPhysics };
    physicsLifecycleRef.current = physicsLifecycle;

    const loadingManager = new THREE.LoadingManager();
    loadingManager.setURLModifier(normalizeWegoAssetUrl);
    loadingManager.onError = () => {
      failSceneLoad('ロボットの3D部品を読み込めませんでした。');
    };
    loadingManager.onLoad = () => {
      if (disposed || loadFailed) {
        if (pendingRoot) disposeObject(pendingRoot);
        if (pendingRoot) scene.remove(pendingRoot);
        pendingRobot = null;
        pendingRoot = null;
        return;
      }
      if (loadCompleted || !pendingRobot || !pendingRoot) return;
      const robot = pendingRobot;
      const root = pendingRoot;
      const loadedBounds = new THREE.Box3().setFromObject(root);
      if (loadedBounds.isEmpty()) return;
      Object.values(robot.joints ?? {}).forEach((joint) => { if (joint) joint.ignoreLimits = true; });
      const bloomMaterials: THREE.MeshStandardMaterial[] = [];
      robot.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        if (belongsToCollisionObject(mesh)) return;
        const sourceMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const isEye = belongsToNamedObject(mesh, EYE_NAMES);
        const isBackLed = belongsToNamedObject(mesh, BACK_LED_NAMES);
        const materials = sourceMaterials.map((source) => {
          if (!isEye && !isBackLed) return createRobotMaterial(source);
          const material = createGlassEyeMaterial(source);
          if (isBackLed) {
            material.color.set(BACK_LED_COLOR);
            material.emissive.set(BACK_LED_COLOR);
            material.emissiveIntensity = 0.65;
          }
          bloomMaterials.push(material);
          return material;
        });
        mesh.material = Array.isArray(mesh.material) ? materials : materials[0];
        mesh.castShadow = !iPad;
        mesh.receiveShadow = !iPad;
      });
      bloomMaterialsRef.current = bloomMaterials;
      robot.traverse((child) => {
        if (!(child as { isURDFCollider?: boolean }).isURDFCollider) return;
        child.visible = false;
        child.userData.filoNativeCollision = true;
        child.traverse((candidate) => {
          candidate.raycast = () => undefined;
        });
        child.traverse((candidate) => {
          const mesh = candidate as THREE.Mesh;
          if (!mesh.isMesh) return;
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(mesh.geometry),
            new THREE.LineBasicMaterial({ color: 0xff3b30, depthTest: false }),
          );
          edges.position.copy(mesh.position);
          edges.quaternion.copy(mesh.quaternion);
          edges.scale.copy(mesh.scale);
          edges.renderOrder = 20;
          edges.frustumCulled = false;
          mesh.parent?.add(edges);
          mesh.visible = false;
        });
      });
      const eyeMaterials = new Set<THREE.MeshStandardMaterial>();
      robot.traverse((child) => {
        if (!EYE_NAMES.test(child.name)) return;
        child.traverse((candidate) => {
          const mesh = candidate as THREE.Mesh;
          const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
          materials.forEach((material) => {
            const standard = material as THREE.MeshStandardMaterial;
            if (standard.emissive) eyeMaterials.add(standard);
          });
        });
      });
      eyeMaterialsRef.current = [...eyeMaterials];
      const eyeLights: ForwardEyeLight[] = [];
      robot.traverse((child) => {
        if (!EYE_NAMES.test(child.name)) return;
        const light = new ForwardEyeLight(child, DEFAULT_EYE_LED_COLOR);
        light.name = `${child.name}_led_light`;
        eyeLights.push(light);
      });
      eyeLightsRef.current = eyeLights;
      lastEyeColorRef.current = null;
      robot.rotation.x = Math.PI / 2;
      robot.updateMatrixWorld(true);
      const center = new THREE.Box3().setFromObject(robot).getCenter(new THREE.Vector3());
      robot.position.sub(center);
      robotRef.current = robot;
      robotRootRef.current = root;
      const applyMazePose = (pose: MazeRobotPose, preserveWorldPosition = false) => {
        if (!mazeStage || !mazeAnchor) return;
        applyMazePoseToAnchor(mazeStage, mazeAnchor, root, pose, preserveWorldPosition);
        if (cameraModeRef.current === 'robot') followRobotCamera(pose);
        else render(true);
      };
      setMazeRobotPoseRef.current = applyMazePose;
      if (mazePoseRef.current) applyMazePose(mazePoseRef.current);
      bodyBaseRotationRef.current = null;
      applyPose(Math.max(0, lastFrameRef.current));
      clampRobotToGround();
      fitCamera(root);
      render(true);
      if (jointDragEnabled) dragControls = createDragControls(robot);
      if (physicsEnabledRef.current) void startPhysics();
      loadCompleted = true;
      clearSceneLoadTimer();
      onSceneReady?.();
    };
    const loader = new URDFLoader(loadingManager);
    loader.parseCollision = true;
    const loaderAbortController = new AbortController();
    loader.fetchOptions = { signal: loaderAbortController.signal };
    loader.workingPath = `${WEGO_URDF_BASE}/`;
    loader.packages = resolveWegoPackagePath;
    loader.load(
      WEGO_URDF_URL,
      (loadedRobot) => {
        const robot = loadedRobot as WegoRobot;
        const root = new THREE.Object3D();
        root.scale.setScalar(10);
        root.add(robot);
        tofPyramid = new THREE.Mesh(
          createTofPyramidGeometry(),
          createTofPyramidMaterial(),
        );
        tofPyramid.name = 'FILO_TOF_SCAN_PYRAMID';
        tofPyramid.visible = false;
        scene.add(tofPyramid);
        tofPyramidEdges = createTofPyramidEdges();
        tofPyramidEdges.name = 'FILO_TOF_SCAN_PYRAMID_EDGES';
        tofPyramidEdges.visible = false;
        scene.add(tofPyramidEdges);
        pendingRobot = robot;
        pendingRoot = root;
        if (disposed) {
          disposeObject(root);
          return;
        }
        if (mazeAnchor) {
          mazeAnchor.add(root);
          scene.add(mazeAnchor);
        } else {
          scene.add(root);
        }
      },
      undefined,
      () => {
        failSceneLoad('ロボットモデルを読み込めませんでした。/models/wego/urdf/wego.urdf を確認してください。');
      },
    );

    const resize = () => {
      const nextWidth = Math.max(1, container.clientWidth);
      const nextHeight = Math.max(1, container.clientHeight);
      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
      if (mazeStage && topView && shouldRefitMazeCameraOnResize(cameraModeRef.current)) fitMazeCamera();
      render();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    return () => {
      disposed = true;
      clearSceneLoadTimer();
      loaderAbortController.abort();
      resizeObserver.disconnect();
      dragControls?.dispose();
      dragControls = null;
      if (physicsLifecycleRef.current === physicsLifecycle) physicsLifecycleRef.current = null;
      stopPhysics();
      if (setSliderHighlightedJointRef.current === setSliderHighlightedJoint) {
        setSliderHighlightedJointRef.current = null;
      }
      highlightMaterial.dispose();
      controls.removeEventListener('change', handleControlsChange);
      controls.dispose();
      transformControls.detach();
      transformControls.dispose();
      scene.remove(transformHelper);
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
      if (renderRef.current === render) renderRef.current = null;
      if (updatePreviewFollowRef.current === updatePreviewFollow) updatePreviewFollowRef.current = null;
      if (captureSnapshotRef.current === takeSnapshot) captureSnapshotRef.current = null;
      snapshotRenderTarget?.dispose();
      snapshotRenderTarget = null;
      snapshotCanvas = null;
      if (pendingRoot) {
        pendingRoot.parent?.remove(pendingRoot);
        disposeObject(pendingRoot);
      }
      if (mazeAnchor) scene.remove(mazeAnchor);
      if (tofPyramid) {
        tofPyramid.geometry.dispose();
        const material = tofPyramid.material;
        if (Array.isArray(material)) material.forEach((item) => item.dispose());
        else material.dispose();
        tofPyramid = null;
      }
      if (tofPyramidEdges) {
        tofPyramidEdges.geometry.dispose();
        const material = tofPyramidEdges.material;
        if (Array.isArray(material)) material.forEach((item) => item.dispose());
        else material.dispose();
        tofPyramidEdges = null;
      }
      robotRef.current = null;
      robotRootRef.current = null;
      setMazeRobotPoseRef.current = null;
      setMazeCameraModeRef.current = null;
      setMazeCameraRotationRef.current = null;
      measureTofDistanceMmRef.current = null;
      bodyBaseRotationRef.current = null;
      eyeMaterialsRef.current = [];
      bloomMaterialsRef.current = [];
      eyeLightsRef.current = [];
      lastFrameRef.current = -1;
      lastEyeColorRef.current = null;
      scene.environment = null;
      environment.dispose();
      pmrem.dispose();
      ground.geometry.dispose();
      (ground.material as THREE.Material).dispose();
      disposePreviewFloor(floor);
      eyeBloom.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      container.replaceChildren();
    };
  }, [applyPose, clampRobotToGround, jointDragEnabled, mazeStage, onBodyRotationChange, onJointSelectionChange, onLoadError, onSceneReady, presentationMode, publishJointControls, topView]);

  return {
    containerRef,
    loadTimeline,
    showFrame,
    setPaused,
    setSelectedJointDegrees,
    setJointValue,
    setJointPoseValue,
    setBodyRotation,
    setEyeLedColor,
    setMazeRobotPose,
    setMazeCameraMode,
    setMazeCameraRotation,
    measureTofDistanceMm,
    captureSnapshot,
  };
}
